import { fechaHora } from '@/shared/utilidades/formato'

import { RelojDelTrabajo } from './RelojDelTrabajo'
import { Rastreo } from './Rastreo'
import { useVisitas } from './useVisitas'
import estilos from './LlegadaYSalida.module.css'

type Props = {
  ordenTrabajoId: string
  estado: string
  /** Ya pidió permiso para empezar sin ubicación y falta que se lo den. */
  solicitaInicio?: boolean
  /** Lo programado, para poder decir cuánto le queda. */
  inicioProgramado?: string | null
  finProgramado?: string | null
  horasAsignadas?: number | null
}

/**
 * El reloj del sitio, visto desde la oficina. SOLO MIRA.
 *
 * Las marcas de campo —«Llegué» y «Terminar»— las hace el técnico desde su
 * teléfono, parado en el sitio: la llegada toma su ubicación y la salida exige
 * fotos de evidencia. La oficina no las marca por él; aquí solo se ve lo que
 * pasó: a qué hora llegó, cuánto lleva y cuándo salió.
 *
 * (Antes esta pantalla tenía un botón «Llegué» para marcar desde la oficina.
 * Se quitó: marcar la llegada de alguien que no está aquí no prueba nada, y el
 * cierre ya es todo del lado del técnico.)
 */
export function LlegadaYSalida({
  ordenTrabajoId,
  estado,
  solicitaInicio = false,
  inicioProgramado = null,
  finProgramado = null,
  horasAsignadas = null,
}: Props) {
  const { visitas, enSitio } = useVisitas(ordenTrabajoId)

  // La llegada que abrió la visita en curso: contra ella se cuenta el tiempo.
  const ultimaLlegada =
    [...visitas].reverse().find((v) => v.tipo === 'llegada')?.momento ?? null

  // Solo tiene sentido sobre un trabajo que ya está programado o corriendo.
  if (!['programada', 'en_ejecucion'].includes(estado) && visitas.length === 0) return null

  return (
    <div className={estilos.zona}>
      <div className={estilos.encabezado}>
        <span className={estilos.rotulo}>Tiempo en el sitio</span>
      </div>

      {/* El reloj: lo programado, a qué hora llegó y cuánto le queda. De solo
          mirar: llegar y terminar se hacen desde el teléfono del técnico. */}
      {enSitio && (
        <>
          <RelojDelTrabajo
            inicioProgramado={inicioProgramado}
            finProgramado={finProgramado}
            horasAsignadas={horasAsignadas}
            llegada={ultimaLlegada}
            onTerminar={() => {}}
            terminando={false}
            puedeTerminar={false}
          />
          <p className={estilos.esperandoPermiso}>
            El técnico termina el trabajo desde su teléfono, adjuntando las fotos de evidencia y el
            comentario de lo hecho.
          </p>
        </>
      )}

      {visitas.length === 0 ? (
        <p className={estilos.vacio}>
          El reloj arranca cuando el técnico toca «Llegué» en su teléfono y este da la ubicación:
          como la salida no siempre se marca, ese punto es la prueba de que estuvo en el sitio. Se
          guarda uno al llegar y otro al salir, nada más.
        </p>
      ) : (
        <ul className={estilos.lista}>
          {visitas.map((visita) => (
            <li key={visita.id} className={estilos.visita}>
              <span
                className={`${estilos.marca} ${
                  visita.tipo === 'llegada' ? estilos.entrada : estilos.salida
                }`}
              >
                {visita.tipo === 'llegada' ? 'Llegó' : 'Salió'}
              </span>

              <span className={estilos.momento}>{fechaHora(visita.momento)}</span>

              {/* Un punto con 800 m de error no prueba nada, y sin decirlo
                  parecería igual de bueno que uno de 5 m. */}
              {visita.latitud === null ? (
                <span className={estilos.sinPunto} title={visita.motivo_sin_punto ?? undefined}>
                  {visita.estimada ? 'salida estimada' : 'sin verificar'}
                </span>
              ) : (
                <span className={estilos.punto}>
                  ±{Math.round(Number(visita.exactitud_m ?? 0))} m
                  {visita.distancia_m !== null && (
                    <span
                      className={Number(visita.distancia_m) > 300 ? estilos.lejos : estilos.cerca}
                    >
                      a {Math.round(Number(visita.distancia_m))} m de la dirección
                    </span>
                  )}
                </span>
              )}

              <span className={estilos.quien}>{visita.marcada_por_nombre}</span>
            </li>
          ))}
        </ul>
      )}

      {solicitaInicio && (
        <p className={estilos.esperandoPermiso}>
          Se pidió permiso para empezar sin ubicación. Esperando al Gerente operativo.
        </p>
      )}

      {/* El rastreo continuo, para no depender de que se acuerden de marcar. */}
      {['programada', 'en_ejecucion'].includes(estado) && <Rastreo />}
    </div>
  )
}
